import React, {ReactElement} from 'react';
import {
  CampaignConfirmationCampaign,
  ServiceChargesDetails
} from '@customink/stores-api-client';
import {
  Typography,
  Button,
  PrinterIcon,
  InfoCircleIcon,
  Tooltip
} from '@customink/pigment-react';
import {IconButton} from '@mui/material';
import {formatCurrency} from '../../utils/helpers';
import './index.scss';
import BuyupOrderDetailsCardSkeleton from './BuyupDetailsCardSkeleton';

const serviceChargesAmount = (value: number) => {
  return Number.isNaN(value) ? '$0.00' : formatCurrency(value);
};

const buyupOrderDetailsSummary = (
  serviceChargesDetails: ServiceChargesDetails
) => {
  const serviceCharges = Number(serviceChargesDetails?.serviceCharges);
  const serviceChargeTax = Number(serviceChargesDetails?.tax);
  const totalServiceCharges = serviceCharges + serviceChargeTax;

  return (
    <div className="buyupOrderDetailsCard-summary">
      <div className="summaryItem">
        <Typography variant="bodyShort1">Subtotal</Typography>
        <Typography variant="bodyShort1">N/A</Typography>
      </div>
      <div className="summaryItem">
        <div>
          <Typography variant="bodyShort1">Service Charges</Typography>
          <br />
          <Typography variant="bodyShort1" className="organizerContribution">
            Organizer Contribution
            <Tooltip
              arrow
              placement="top-end"
              title="This is the remaining amount needed to complete your order at a quantity less than your initial goal.">
              <IconButton>
                <InfoCircleIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          </Typography>
        </div>
        <Typography variant="bodyShort1">
          {serviceChargesAmount(serviceCharges)}
        </Typography>
      </div>
      <div className="summaryItem">
        <Typography variant="bodyShort1">Shipping & Handling</Typography>
        <Typography variant="bodyShort1">N/A</Typography>
      </div>
      <div className="summaryItem">
        <Typography variant="bodyShort1">Taxes</Typography>
        <Typography variant="bodyShort1">
          {serviceChargesAmount(serviceChargeTax)}
        </Typography>
      </div>
      <div className="summaryItem bold">
        <Typography variant="h5" className="bold">
          Total
        </Typography>
        <Typography variant="h5" className="bold">
          {serviceChargesAmount(totalServiceCharges)}
        </Typography>
      </div>
    </div>
  );
};

const buyupCreditCardAndPrint = (storeSlug: string, creditCardfour: string) => {
  return (
    <div className="buyupOrderDetailsCard-creditCard">
      <div className="creditCardItem">
        {creditCardfour ? (
          <Typography variant="bodyShort1">{`Paid via Credit Card *${creditCardfour}`}</Typography>
        ) : null}
      </div>
      <Button
        variant="secondary"
        className="orderPrintButton"
        startIcon={<PrinterIcon />}
        onClick={() => window.print()}>
        Print Invoice
      </Button>
      <Button
        className="returnToDashboardButton"
        href={`/go/${storeSlug}/manage/dashboard`}>
        Return to Dashboard
      </Button>
    </div>
  );
};

type BuyupOrderDetailsCardProps = {
  storeSlug: string;
  campaign: CampaignConfirmationCampaign;
  serviceChargesDetails: ServiceChargesDetails;
  placeholder?: boolean;
  mobile?: boolean;
};

const BuyupOrderDetailsCard: React.FC<BuyupOrderDetailsCardProps> = ({
  storeSlug,
  campaign,
  serviceChargesDetails,
  placeholder,
  mobile
}: BuyupOrderDetailsCardProps): ReactElement => {
  const orderDetailsClassname = `buyupOrderDetailsCard${
    mobile ? ' mobile' : ''
  }`;

  return placeholder ? (
    <BuyupOrderDetailsCardSkeleton />
  ) : (
    <div className={orderDetailsClassname}>
      <Typography className="orderNumber bold">
        Order #{campaign?.id}
      </Typography>
      {buyupOrderDetailsSummary(serviceChargesDetails)}
      {buyupCreditCardAndPrint(
        storeSlug,
        serviceChargesDetails?.creditCardLastFour
      )}
    </div>
  );
};

BuyupOrderDetailsCard.defaultProps = {
  mobile: false,
  placeholder: false
};

export default BuyupOrderDetailsCard;
